import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import * as Services from '$/services';
import { UserAdminService } from '$/services/UserAdminService';
import BixButton from '$/components/global/bixButton';
import { ReactComponent as FunnelIcon } from '$/assets/icons/Funnel.svg';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { toastError, toastInfo, toastSuccess } from '$/store/toast/actions';
import { AppState } from '$/store';
import { getUserStatusState } from '$/store/profile/selectors';
import './index.scss';
import { AssignableUser, DataSiteEmailTrackingData } from '$/core/models';
import { DataSiteEmailTrackingGroupedRecordStatus, UserStatus } from '$/core/enums';
import StatusCards from './StatusCards';
import WorkflowGrid from './WorkflowGrid';
import WorkflowUserSection from './WorkflowUserSection';

const NON_COMPLETE_STATUSES = Object.values(DataSiteEmailTrackingGroupedRecordStatus).filter(
  s => s !== DataSiteEmailTrackingGroupedRecordStatus.Complete
);

const getStatusesToFetch = (status: DataSiteEmailTrackingGroupedRecordStatus | null) =>
  status === null ? NON_COMPLETE_STATUSES : [status];

interface IWorkflowManagementProps {
  userProfile?: Bixby.Models.Profile;
  userStatus?: UserStatus | null;
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
  dispatchToastInfo: (message: string) => void;
}

const WorkflowManagement: React.FC<IWorkflowManagementProps> = props => {
  const { userProfile, userStatus, dispatchToastError, dispatchToastSuccess, dispatchToastInfo } = props;
  const navigate = useNavigate();
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [isStatusCountsLoading, setIsStatusCountsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [dirtyRows, setDirtyRows] = useState<Map<string, DataSiteEmailTrackingGroupedRecordStatus>>(new Map());
  const [originalStatus, setOriginalStatus] = useState<Map<string, DataSiteEmailTrackingGroupedRecordStatus>>(
    new Map()
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [emailTrackingTasks, setEmailTrackingTasks] = useState<DataSiteEmailTrackingData[]>([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('All');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('All Assignees');
  const [selectedStatus, setSelectedStatus] = useState<DataSiteEmailTrackingGroupedRecordStatus | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const fetchStatusCounts = useCallback(async () => {
    try {
      setIsStatusCountsLoading(true);
      const counts = await Services.DataSiteService.getEmailTrackingStatusCounts();
      const countsMap: Record<string, number> = {
        all: counts.reduce((sum, item) => sum + item.count, 0),
      };
      counts.forEach(item => {
        countsMap[item.status] = item.count;
      });
      setStatusCounts(countsMap);
    } catch {
      dispatchToastError('Failed to load status counts');
    } finally {
      setIsStatusCountsLoading(false);
    }
  }, [dispatchToastError]);

  const fetchTrackingData = useCallback(async () => {
    try {
      setIsGridLoading(true);
      setErrorMessage(undefined);
      const data = await Services.DataSiteService.getEmailTrackingRecordsByStatus(getStatusesToFetch(selectedStatus));
      setEmailTrackingTasks(data);
      const statusMap = new Map<string, DataSiteEmailTrackingGroupedRecordStatus>();
      data.forEach(task => {
        statusMap.set(task.groupId, task.status ?? DataSiteEmailTrackingGroupedRecordStatus.Received);
      });
      setOriginalStatus(statusMap);
    } catch {
      setErrorMessage('Failed to load email tracking data. Please try again later.');
    } finally {
      setIsGridLoading(false);
    }
  }, [selectedStatus]);

  const fetchAssignees = useCallback(async () => {
    try {
      const users = await UserAdminService.getAssignees();
      setAssignableUsers(users);
    } catch {
      dispatchToastError('Failed to load assignees');
    }
  }, [dispatchToastError]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  useEffect(() => {
    fetchTrackingData();
  }, [fetchTrackingData]);

  useEffect(() => {
    fetchAssignees();
  }, [fetchAssignees, userStatus]);

  // Filter tasks based on selected status
  const filteredTasks =
    selectedStatusFilter === 'All'
      ? emailTrackingTasks
      : emailTrackingTasks.filter(task => task.status === selectedStatusFilter);

  const handleStatusCardClick = (status: DataSiteEmailTrackingGroupedRecordStatus | null) => {
    setSelectedStatus(status);
  };

  const handleStatusChange = (groupId: string, newStatus: DataSiteEmailTrackingGroupedRecordStatus) => {
    const originalStatusValue = originalStatus.get(groupId);

    if (originalStatusValue && originalStatusValue !== newStatus) {
      // Status changed from original - mark as dirty
      setDirtyRows(prev => {
        const newMap = new Map(prev);
        newMap.set(groupId, newStatus);
        return newMap;
      });
    } else if (originalStatusValue && originalStatusValue === newStatus) {
      // User reverted to original value - remove from dirty rows
      setDirtyRows(prev => {
        const newMap = new Map(prev);
        newMap.delete(groupId);
        return newMap;
      });
    }
  };

  const handleRefreshData = useCallback(async () => {
    await Promise.all([fetchTrackingData(), fetchStatusCounts(), fetchAssignees()]);
  }, [fetchTrackingData, fetchStatusCounts, fetchAssignees]);

  const handleSaveChanges = async () => {
    if (dirtyRows.size === 0) return;

    try {
      setIsSaving(true);
      const statusUpdates = Array.from(dirtyRows.entries()).map(([groupId, status]) => ({
        groupId,
        status,
      }));

      await Services.DataSiteService.updateEmailTrackingStatus({ statusUpdates });

      // Success - clear all dirty rows
      dispatchToastSuccess(
        `${statusUpdates.length} ${statusUpdates.length === 1 ? 'task' : 'tasks'} updated successfully`
      );
      setDirtyRows(new Map());
      await handleRefreshData();
    } catch {
      dispatchToastError('Failed to update tasks. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const statusTabs = [
    { label: 'All', value: 'All' },
    ...Object.values(DataSiteEmailTrackingGroupedRecordStatus)
      .filter(status => statusCounts[status] >= 1)
      .map(status => ({
        label: status.replace(/([A-Z])/g, ' $1').trim(),
        value: status,
      })),
  ];

  const getActiveTabCount = () => {
    if (selectedStatusFilter === 'All') {
      return statusCounts.all;
    }
    return statusCounts[selectedStatusFilter] || 0;
  };

  // Navigation guard for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRows.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirtyRows]);

  if (isStatusCountsLoading) {
    return <Loading pageLevel />;
  }

  return (
    <div className="workflow_management_container">
      <div className="workflow_management_header">
        <HeadingBlock title={'Workflow Management Tool'} />
        {userProfile && <WorkflowUserSection />}
        <div className="workflow_management_header_actions">
          <BixButton
            className="workflow_management_save_button"
            isLoading={isSaving}
            disabled={isSaving || dirtyRows.size === 0}
            onClick={handleSaveChanges}>
            Save {dirtyRows.size} {dirtyRows.size === 1 ? 'Change' : 'Changes'}
          </BixButton>
          <BixButton onClick={() => navigate('preferred-clients-order')}>Preferred Client Order</BixButton>
        </div>
      </div>
      <StatusCards statusCounts={statusCounts} selectedStatus={selectedStatus} onStatusClick={handleStatusCardClick} />
      <div className="workflow_management_tasks_container">
        <div className="workflow_management_tasks_summary">
          <div className="workflow_management_tasks_summary_left">
            <h3>Doc Upload Tasks</h3>
            <span>
              {emailTrackingTasks.length} {emailTrackingTasks.length === 1 ? 'task' : 'tasks'} -{' '}
              {selectedStatus ? selectedStatus.replace(/([A-Z])/g, ' $1').trim() : 'All Except Complete'}
            </span>
          </div>
          <div className="workflow_management_toolbar">
            <div className="workflow_management_filter_icon">
              <FunnelIcon />
            </div>
            <select
              className="workflow_management_assignee_dropdown"
              value={selectedAssignee}
              onChange={e => setSelectedAssignee(e.target.value)}>
              <option value="All Assignees">All Assignees</option>
              {assignableUsers.map(assignee => (
                <option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </option>
              ))}
            </select>
            <div className="workflow_management_status_tabs">
              {statusTabs.map(tab => (
                <button
                  key={tab.value}
                  className={`workflow_management_status_tab ${selectedStatusFilter === tab.value ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter(tab.value)}>
                  {tab.label}
                  {selectedStatusFilter === tab.value && (
                    <span className="workflow_management_badge">{getActiveTabCount()}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
        {isGridLoading ? (
          <Loading />
        ) : emailTrackingTasks && !Bixby.Utils.isNullOrEmpty(emailTrackingTasks) ? (
          <section className="workflow_management_tasks_list">
            <WorkflowGrid
              filteredTasks={filteredTasks}
              assigneeList={assignableUsers}
              dirtyRows={dirtyRows}
              onStatusChange={handleStatusChange}
              onRefreshData={handleRefreshData}
              dispatchToastError={dispatchToastError}
              dispatchToastSuccess={dispatchToastSuccess}
              dispatchToastInfo={dispatchToastInfo}
            />
          </section>
        ) : (
          <>
            {errorMessage && <div>{errorMessage}</div>}
            <div className="workflow_management_no_data">
              <NoDataFound dataType="tasks" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { userStatus } = getUserStatusState(state);
  return {
    userProfile: state.profileState.profile,
    userStatus,
  };
};
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastInfo: (message: string) => dispatch(toastInfo(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowManagement);
